/**
 * Centralized API client with authentication and error handling
 */
import axios from 'axios';
import logger from '../utils/logger';
import { RESOURCE_BOARD_CONFIG } from '../utils/config';

class ApiClient {
  constructor() {
    this.baseURL = RESOURCE_BOARD_CONFIG.api.baseUrl;
    this.timeout = RESOURCE_BOARD_CONFIG.api.timeout;

    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Debug logging disabled for performance
        // logger.debug('API Request:', {
        //   method: config.method,
        //   url: config.url,
        //   data: config.data
        // });

        return config;
      },
      (error) => {
        logger.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => {
        // Debug logging disabled for performance
        // logger.debug('API Response:', {
        //   status: response.status,
        //   url: response.config.url,
        //   data: response.data
        // });
        return response;
      },
      async (error) => {
        logger.error('API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          data: error.response?.data
        });

        // Handle 401 Unauthorized - token expired
        if (error.response?.status === 401) {
          logger.warn('Token expired, redirecting to login...');
          // Clear tokens
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Redirect to login
          window.location.href = '/login';
        }

        // Handle network errors
        if (!error.response) {
          logger.error('Network error - no response from server');
          throw new Error('Network error. Please check your connection.');
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  async get(url, config = {}) {
    try {
      const response = await this.client.get(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * POST request
   */
  async post(url, data, config = {}) {
    try {
      const response = await this.client.post(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put(url, data, config = {}) {
    try {
      const response = await this.client.put(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  async patch(url, data, config = {}) {
    try {
      const response = await this.client.patch(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete(url, config = {}) {
    try {
      const response = await this.client.delete(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    const message = error.response?.data?.detail
      || error.response?.data?.message
      || error.message
      || 'An unexpected error occurred';

    logger.error('API Error Handler:', message);

    throw new Error(message);
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;
